import { useParams, Link } from "react-router-dom";
import { Check, ArrowLeft, FileText, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import EnergyDiagram from "@/components/EnergyDiagram";
import CTASection from "@/components/CTASection";
import { useSite, useBrand } from "@/context/SiteContext";
import { useSeo } from "@/lib/seo";
import { PRODUCTS, brandify } from "@/data/content";

export default function ProductDetail() {
  const { slug } = useParams();
  const p = PRODUCTS.find((x) => x.slug === slug);
  const { openQuote, waLink } = useSite();
  const brand = useBrand();
  const intro = brandify(p?.intro, brand.name);
  useSeo({
    title: p ? `${p.name} | ${brand.name}` : `Product | ${brand.name}`,
    description: p ? `${p.hero}. ${intro?.slice(0, 150)}` : "",
    canonical: `https://${brand.domain}/products/${slug}`,
  });

  if (!p) return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-slate-300">Product not found. <Link to="/products" className="text-emerald-400">Back to Products</Link></div>;

  return (
    <div>
      <PageHeader label={p.tag} title={p.hero} sub={p.sub} image={p.image} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <Reveal>
            <Link to="/products" className="text-emerald-400 text-sm inline-flex items-center gap-1 mb-4"><ArrowLeft size={15} /> All Products</Link>
            <p className="text-slate-300 leading-relaxed text-lg">{intro}</p>
          </Reveal>

          {p.benefits && (
            <Reveal>
              <h2 className="font-display text-2xl font-bold text-white mb-4">{p.groups ? "Solution Range" : "Key Benefits"}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {p.benefits.map((b) => (
                  <div key={b} className="flex items-start gap-3 text-slate-300"><Check className="text-emerald-400 mt-0.5 shrink-0" size={18} /> {b}</div>
                ))}
              </div>
            </Reveal>
          )}

          {p.groups && (
            <Stagger className="grid sm:grid-cols-2 gap-5">
              {p.groups.map((g) => (
                <motion.div key={g.title} variants={staggerItem} className="card-surface p-6">
                  <div className="font-display font-semibold text-white text-lg mb-3">{g.title}</div>
                  <ul className="space-y-1.5">{g.items.map((it) => <li key={it} className="text-sm text-slate-400">— {it}</li>)}</ul>
                </motion.div>
              ))}
            </Stagger>
          )}

          {p.applications && (
            <Reveal>
              <h2 className="font-display text-2xl font-bold text-white mb-4">Applications</h2>
              <div className="flex flex-wrap gap-2">{p.applications.map((a) => <span key={a} className="badge-terminal">{a}</span>)}</div>
            </Reveal>
          )}

          {p.slug === "hybrid-solar-inverters" && (
            <Reveal className="card-surface p-6"><div className="mono-label mb-3">Interactive System Flow</div><EnergyDiagram /></Reveal>
          )}

          {p.note && (
            <div className="card-surface p-5 border-amber-500/30 !bg-amber-500/5">
              <div className="mono-label !text-amber-400">Note</div>
              <p className="text-sm text-slate-300 mt-1">{p.note}</p>
            </div>
          )}
        </div>

        <aside className="space-y-5">
          <div className="card-surface p-6 sticky top-28">
            <div className="mono-label">Enquire</div>
            <h3 className="font-display text-xl font-bold text-white mt-1 mb-2">Get a Quote for {p.name}</h3>
            <p className="text-sm text-slate-400 mb-4">Share your load and application — we'll help you choose the right product and capacity.</p>
            <button onClick={() => openQuote(p.name)} className="btn-primary w-full mb-3" data-testid="product-quote-button"><FileText size={16} /> Get a Quote</button>
            <a href={waLink(`Hello ${brand.name}, I'm interested in ${p.name}.`)} target="_blank" rel="noreferrer" className="btn-ghost w-full" data-testid="product-whatsapp-button"><MessageCircle size={16} /> WhatsApp Us</a>
            <div className="mt-5 pt-5 border-t border-white/10 text-xs text-slate-500">Detailed specifications, datasheets and warranty are shared on request or added once actual product data is available. <span className="text-slate-400">Product details coming soon.</span></div>
          </div>
        </aside>
      </section>
      <CTASection />
    </div>
  );
}
